/**
 * Cart Store
 * Manages shopping cart state with validation and business rules
 *
 * Rules:
 * - Cart is global across catalog (not per screen)
 * - Persists to Firestore (per user, syncs across devices)
 * - Only works for authenticated users
 * - Order unit: units only (no cartons selector)
 * - Quantity rules based on item.onlyBox:
 *   - onlyBox == 0: must be multiples of boxSize
 *   - onlyBox == 1: any integer >= 1
 * - Pricing is live (computed from catalog)
 * - Items without price cannot be added
 * - Same makat merges into existing line
 */
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useAuthStore } from './auth.js'
import { useCatalogStore } from './catalog.js'
import { usePromotionsStore } from './promotions.js'
import { useAmountDiscountsStore } from './amountDiscounts.js'
import logger from '../../core/services/logger.js'
import { saveCart, loadSavedCart, clearSavedCart } from '../../core/services/savedCart.js'
import { success, warning } from '../../core/composables/useToast.js'
import {
  computeCumulativeMadregotApplication,
  getNextStepHint,
  isMadregotPromo,
} from '../../core/utils/promoCalc.js'
import {
  computeCumulativeAmountDiscountApplication,
  getNextDiscountTierHint,
} from '../../core/utils/discountCalc.js'
import { VAT_MULTIPLIER, DEPOSIT_COEFF } from '../../core/config/pricingConstants.js'

const log = logger.createScoped('CartStore')

// ============================================
// Helper Functions
// ============================================

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
function isUserAuthenticated() {
  const authStore = useAuthStore()
  return authStore.isAuthenticated
}

/**
 * Get catalog item by makat from loaded catalog
 * @param {string} makat
 * @returns {Object|null}
 */
function getCatalogItemByMakat(makat) {
  const catalogStore = useCatalogStore()
  return catalogStore.itemsByMakat.get(makat) || null
}

/**
 * Parse boxSize from item (stored as string)
 * @param {Object} item
 * @returns {number}
 */
function parseBoxSize(item) {
  if (!item?.boxSize) return 0
  const parsed = parseInt(item.boxSize, 10)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Check if item requires box multiples (onlyBox == 0)
 * @param {Object} item
 * @returns {boolean}
 */
function requiresBoxMultiples(item) {
  // onlyBox: 0, '0', false, null, or undefined means box multiples required
  // onlyBox: 1, '1', or true means any integer >= 1 is allowed
  const val = item?.onlyBox
  return val == null || val === 0 || val === '0' || val === false
}

/**
 * Validate and normalize quantity for an item
 * Policy B: Reject if not valid multiple (don't auto-normalize)
 * @param {Object} item - Catalog item
 * @param {number} qty - Requested quantity
 * @returns {{ valid: boolean, qty: number, message?: string }}
 */
function validateQtyForItem(item, qty) {
  // Ensure integer
  const intQty = Math.floor(qty)

  if (intQty <= 0) {
    return { valid: false, qty: 0, message: 'INVALID_QUANTITY' }
  }

  // Check onlyBox rule
  if (requiresBoxMultiples(item)) {
    const boxSize = parseBoxSize(item)

    if (boxSize <= 0) {
      return { valid: false, qty: 0, message: 'INVALID_BOXSIZE' }
    }

    // Policy B: Reject if not a valid multiple
    if (intQty % boxSize !== 0) {
      return {
        valid: false,
        qty: 0,
        message: `QTY_NOT_BOX_MULTIPLE:${boxSize}`,
      }
    }
  }

  return { valid: true, qty: intQty }
}

/**
 * Get the step size for inc/dec operations
 * For onlyBox items, step is boxSize; otherwise 1
 * @param {Object} item
 * @returns {number}
 */
function getStepForItem(item) {
  if (requiresBoxMultiples(item)) {
    const boxSize = parseBoxSize(item)
    return boxSize > 0 ? boxSize : 1
  }
  return 1
}

/**
 * Create a new cart line object
 * @param {string} makat
 * @param {number} qty
 * @returns {Object}
 */
function createCartLine(makat, qty) {
  const now = Date.now()
  return {
    makat,
    qty,
    addedAt: now,
    updatedAt: now,
    promo: {
      eligiblePromotionIds: [],
      appliedPromotionId: null,
      meta: {},
    },
  }
}

// ============================================
// Store Definition
// ============================================

export const useCartStore = defineStore('cart', () => {
  // ============================================
  // State
  // ============================================

  /**
   * Cart lines indexed by makat for O(1) lookup
   * Using reactive object instead of Map for Vue reactivity
   */
  const linesByMakat = ref({})

  /**
   * Timestamp when cart was created
   */
  const createdAt = ref(Date.now())

  /**
   * Last error message (null if no error)
   */
  const error = ref(null)

  /**
   * Timestamp of last successful mutation
   */
  const lastChangedAt = ref(null)

  /**
   * Flag indicating cart is being restored from Firestore
   */
  const isRestoring = ref(false)

  /**
   * Flag indicating cart drawer is open (for tutorial system)
   */
  const isCartOpen = ref(false)

  /**
   * List of items that could not be restored
   * [{makat, name, reason: 'not_found'|'no_price'|'invalid_qty'}]
   */
  const restorationIssues = ref([])

  /**
   * Flag to prevent saving during restoration
   */
  let isRestorationInProgress = false

  /**
   * Debounce timeout for saving to Firestore
   */
  let saveTimeout = null

  // ============================================
  // Getters / Computed
  // ============================================

  /**
   * Array of cart lines, ordered by addedAt ascending (stable order)
   */
  const linesArray = computed(() => {
    return Object.values(linesByMakat.value).sort((a, b) => a.addedAt - b.addedAt)
  })

  /**
   * Number of unique makats in cart
   */
  const uniqueCount = computed(() => {
    return Object.keys(linesByMakat.value).length
  })

  /**
   * Total quantity sum across all lines (for bubble count)
   */
  const itemsCount = computed(() => {
    return linesArray.value.reduce((sum, line) => sum + line.qty, 0)
  })

  /**
   * Get effective price info for a cart line
   * @deprecated Use pricedCartLines for promo-aware pricing
   * This now returns catalog price only for backwards compatibility
   * @param {string} makat
   * @param {number} qty
   * @returns {{ unitPrice: number, isPromo: boolean, promo: Object | null, step: Object | null, catalogPrice: number | null }}
   */
  function getLinePriceInfo(makat, qty) {
    const item = getCatalogItemByMakat(makat)
    if (!item?.price) {
      return { unitPrice: 0, isPromo: false, promo: null, step: null, catalogPrice: null }
    }

    const catalogPrice = parseFloat(item.price)
    if (isNaN(catalogPrice)) {
      return { unitPrice: 0, isPromo: false, promo: null, step: null, catalogPrice: null }
    }

    // Return catalog price - promo pricing is handled by pricedCartLines
    return { unitPrice: catalogPrice, isPromo: false, promo: null, step: null, catalogPrice }
  }

  /**
   * Subtotal: sum of (effectivePrice * qty) for all lines
   * Uses promo pricing when available, falls back to catalog price
   */
  const subtotal = computed(() => {
    let total = 0
    for (const line of linesArray.value) {
      const priceInfo = getLinePriceInfo(line.makat, line.qty)
      if (priceInfo.unitPrice > 0) {
        total += priceInfo.unitPrice * line.qty
      }
    }
    return total
  })

  /**
   * Subtotal without promo (for showing savings)
   */
  const subtotalWithoutPromo = computed(() => {
    let total = 0
    for (const line of linesArray.value) {
      const item = getCatalogItemByMakat(line.makat)
      if (item?.price != null) {
        const price = parseFloat(item.price)
        if (!isNaN(price)) {
          total += price * line.qty
        }
      }
    }
    return total
  })

  /**
   * Total savings from promotions
   */
  const promoSavings = computed(() => {
    return Math.max(0, subtotalWithoutPromo.value - subtotal.value)
  })

  // ============================================
  // Cumulative Promo Getters
  // ============================================

  /**
   * Compute pricing breakdown for a single unit
   * @param {number} unitPricePreVatPreDeposit - Base unit price (no VAT, no deposit)
   * @param {Object} item - Catalog item (for deposit info)
   * @returns {Object} Pricing breakdown
   */
  function computeLinePricing(unitPricePreVatPreDeposit, item) {
    const pikadon = item?.pikadon ? parseFloat(item.pikadon) : 0
    const depositPerUnit = isNaN(pikadon) ? 0 : pikadon * DEPOSIT_COEFF

    const pricePreVat = unitPricePreVatPreDeposit
    const priceWithVat = pricePreVat * VAT_MULTIPLIER
    const depositWithVat = depositPerUnit * VAT_MULTIPLIER

    return {
      unitPricePreVatPreDeposit,
      depositPerUnit,
      pricePreVat,
      priceWithVat,
      depositWithVat,
      totalPerUnitWithVat: priceWithVat + depositWithVat,
    }
  }

  /**
   * pricedCartLines: Display lines expanded from cart lines
   * Each base makat can produce:
   * - promo line (if promoQty > 0)
   * - regular line (if regularQty > 0), including over-max splits
   * - discount line (if amount discount applies and no promo)
   *
   * Application process:
   * 1. Load eligible active promos in priority order (end_date ASC, id ASC)
   * 2. Iterate promos; compute cumulative application
   * 3. Create display lines from allocations
   * 4. Track assigned makats to prevent stacking
   * 5. For remaining makats, check for amount discounts (cumulative by paritMerakez)
   * 6. Add remaining makats as regular or discounted lines
   */
  const pricedCartLines = computed(() => {
    const catalogStore = useCatalogStore()
    const promotionsStore = usePromotionsStore()
    const amountDiscountsStore = useAmountDiscountsStore()

    const displayLines = []
    const assignedMakats = new Set() // Track makats already assigned to a promo

    // Build cartLinesByMakat object for cumulative computation (include addedAt for allocation ordering)
    const cartLinesByMakat = {}
    for (const line of linesArray.value) {
      cartLinesByMakat[line.makat] = { qty: line.qty, addedAt: line.addedAt }
    }

    // Get eligible promos in priority order
    let eligiblePromos = []
    try {
      eligiblePromos = promotionsStore.getEligibleActivePromos()
    } catch {
      // Promotions store not ready
    }

    // Process each promo
    for (const promo of eligiblePromos) {
      // Only process madregot promos for now
      if (!isMadregotPromo(promo)) continue

      // Compute cumulative application
      const application = computeCumulativeMadregotApplication({
        promo,
        cartLinesByMakat,
      })

      // If no step applies, skip this promo
      if (!application.appliedStep) continue

      // Process allocations for each makat in this promo
      for (const [makat, allocation] of Object.entries(application.allocationsByMakat)) {
        // Skip if makat already assigned to an earlier promo (no stacking)
        if (assignedMakats.has(makat)) continue

        // Skip if this makat has no qty in cart
        if (allocation.promoQty === 0 && allocation.regularQty === 0) continue

        // Get catalog item
        const item = catalogStore.itemsByMakat.get(makat)
        if (!item) continue

        const catalogPrice = parseFloat(item.price)
        if (isNaN(catalogPrice)) continue

        // Mark makat as assigned
        assignedMakats.add(makat)

        // Create promo line (if promoQty > 0)
        if (allocation.promoQty > 0) {
          const promoUnitPrice = application.appliedStep.unitPrice
          const pricing = computeLinePricing(promoUnitPrice, item)

          // Compute next step hint for this promo
          const promoHint = getNextStepHint(promo, application.totalQty)

          displayLines.push({
            displayKey: `${makat}_promo_${promo.id}`,
            makat,
            qty: allocation.promoQty,
            name: item.name || item.description || makat,
            image: item.image || null,
            barcode: item.barcode || null,
            boxSize: item.boxSize || null,
            unitPricePreVatPreDeposit: promoUnitPrice,
            catalogPrice,
            pricing,
            isPromoLine: true,
            isDiscountLine: false,
            promo: {
              id: promo.id,
              label: application.meta.label,
              description: application.meta.description,
              stepDescription: application.appliedStep.description,
              first_order: application.meta.first_order,
              weeklyBenefit: application.meta.weeklyBenefit,
              max: application.maxQty,
            },
            nextStepHint: promoHint
              ? { unitsNeeded: promoHint.unitsNeeded, nextPrice: promoHint.nextStep.price }
              : null,
            discount: null,
            regularReason: null,
            relatedPromoId: promo.id,
            // For UI: cart line reference
            baseMakat: makat,
            addedAt: linesByMakat.value[makat]?.addedAt || Date.now(),
          })
        }

        // Create regular line for over-max qty (if regularQty > 0)
        if (allocation.regularQty > 0) {
          const pricing = computeLinePricing(catalogPrice, item)

          displayLines.push({
            displayKey: `${makat}_regular_overmax_${promo.id}`,
            makat,
            qty: allocation.regularQty,
            name: item.name || item.description || makat,
            image: item.image || null,
            barcode: item.barcode || null,
            boxSize: item.boxSize || null,
            unitPricePreVatPreDeposit: catalogPrice,
            catalogPrice,
            pricing,
            isPromoLine: false,
            isDiscountLine: false,
            promo: null,
            discount: null,
            regularReason: 'over_max',
            relatedPromoId: promo.id,
            // For UI: cart line reference
            baseMakat: makat,
            addedAt: linesByMakat.value[makat]?.addedAt || Date.now(),
          })
        }
      }
    }

    // Build filtered cart lines excluding promo-assigned items for discount calculation
    const nonPromoCartLinesByMakat = {}
    for (const [makat, lineData] of Object.entries(cartLinesByMakat)) {
      if (!assignedMakats.has(makat)) {
        nonPromoCartLinesByMakat[makat] = lineData
      }
    }

    // Add remaining makats (not in any promo) - check for amount discounts
    for (const line of linesArray.value) {
      if (assignedMakats.has(line.makat)) continue

      const item = catalogStore.itemsByMakat.get(line.makat)
      if (!item) continue

      const catalogPrice = parseFloat(item.price)
      if (isNaN(catalogPrice)) continue

      // Check for amount discount (cumulative by paritMerakez)
      const paritMerakez = item.paritMerakez
      const discountTiers = paritMerakez
        ? amountDiscountsStore.getDiscountTiers(paritMerakez)
        : null

      let effectivePrice = catalogPrice
      let discountInfo = null
      let nextDiscountHint = null

      if (discountTiers && discountTiers.length > 0) {
        // Compute cumulative discount application
        const discountApplication = computeCumulativeAmountDiscountApplication({
          paritMerakez,
          basePrice: catalogPrice,
          discounts: discountTiers,
          cartLinesByMakat: nonPromoCartLinesByMakat,
          itemsByMakat: catalogStore.itemsByMakat,
        })

        if (discountApplication.isDiscounted) {
          effectivePrice = discountApplication.effectiveUnitPrice
          discountInfo = {
            paritMerakez,
            discountPercent: discountApplication.discountPercent,
            appliedTier: discountApplication.appliedTier,
            totalQty: discountApplication.totalQty,
            savings: discountApplication.savings,
          }
        }

        // Compute next tier hint (works whether or not a tier is currently active)
        const tierHint = getNextDiscountTierHint(discountTiers, discountApplication.totalQty)
        if (tierHint) {
          nextDiscountHint = {
            unitsNeeded: tierHint.unitsNeeded,
            nextDiscountPercent: tierHint.nextTier.discountPercent,
          }
        }
      }

      const pricing = computeLinePricing(effectivePrice, item)

      displayLines.push({
        displayKey: `${line.makat}_${discountInfo ? 'discount' : 'regular'}`,
        makat: line.makat,
        qty: line.qty,
        name: item.name || item.description || line.makat,
        image: item.image || null,
        barcode: item.barcode || null,
        boxSize: item.boxSize || null,
        unitPricePreVatPreDeposit: effectivePrice,
        catalogPrice,
        pricing,
        isPromoLine: false,
        isDiscountLine: !!discountInfo,
        promo: null,
        discount: discountInfo,
        nextDiscountHint,
        regularReason: null,
        relatedPromoId: null,
        baseMakat: line.makat,
        addedAt: line.addedAt,
      })
    }

    // Sort by addedAt to maintain stable order
    displayLines.sort((a, b) => {
      // Group by baseMakat first (promo line before regular over-max line)
      if (a.baseMakat !== b.baseMakat) {
        return a.addedAt - b.addedAt
      }
      // Promo line comes before regular over-max line for same makat
      if (a.isPromoLine && !b.isPromoLine) return -1
      if (!a.isPromoLine && b.isPromoLine) return 1
      return 0
    })

    return displayLines
  })

  /**
   * pricedCartGroups: Groups pricedCartLines by makat for unified UI rendering
   * Each group represents ONE row in the cart UI, even when a makat has
   * both promo and over-max portions.
   *
   * Group shape:
   * {
   *   makat,
   *   totalQty,                // from linesByMakat[makat].qty
   *   item: { name, image },   // from catalog
   *   promoLine: DisplayLine|null,
   *   discountLine: DisplayLine|null, // amount discount line
   *   overMaxLine: DisplayLine|null,  // regularReason === 'over_max'
   *   regularLine: DisplayLine|null,  // normal regular line when no promo/discount
   *   isUnifiedOverMaxCard: boolean,  // promoLine && overMaxLine
   *   displayKey: string,
   *   addedAt: number,
   * }
   */
  const pricedCartGroups = computed(() => {
    const catalogStore = useCatalogStore()
    const lines = pricedCartLines.value

    // Group lines by baseMakat
    const groupsByMakat = new Map()

    for (const line of lines) {
      const makat = line.baseMakat || line.makat
      if (!groupsByMakat.has(makat)) {
        groupsByMakat.set(makat, {
          makat,
          promoLine: null,
          discountLine: null,
          overMaxLine: null,
          regularLine: null,
        })
      }

      const group = groupsByMakat.get(makat)

      if (line.isPromoLine) {
        group.promoLine = line
      } else if (line.isDiscountLine) {
        group.discountLine = line
      } else if (line.regularReason === 'over_max') {
        group.overMaxLine = line
      } else {
        group.regularLine = line
      }
    }

    // Build final groups array
    const groups = []

    for (const [makat, group] of groupsByMakat) {
      const baseLine = linesByMakat.value[makat]
      const item = catalogStore.itemsByMakat.get(makat)

      groups.push({
        makat,
        totalQty: baseLine?.qty || 0,
        item: item
          ? {
              name: item.name || item.description || makat,
              image: item.image || null,
              price: item.price,
              pikadon: item.pikadon,
              boxSize: item.boxSize,
              onlyBox: item.onlyBox,
              paritMerakez: item.paritMerakez,
            }
          : null,
        promoLine: group.promoLine,
        discountLine: group.discountLine,
        overMaxLine: group.overMaxLine,
        regularLine: group.regularLine,
        isUnifiedOverMaxCard: !!(group.promoLine && group.overMaxLine),
        promoMax: group.promoLine?.promo?.max || null,
        promoGroupId: group.promoLine?.promo?.id || null,
        discountInfo: group.discountLine?.discount || null,
        nextPromoHint: group.promoLine?.nextStepHint || null,
        nextDiscountHint:
          group.discountLine?.nextDiscountHint || group.regularLine?.nextDiscountHint || null,
        displayKey: `group_${makat}`,
        addedAt: baseLine?.addedAt || Date.now(),
        baseAddedAt: baseLine?.addedAt || Date.now(),
      })
    }

    // Sort by addedAt to maintain stable order
    groups.sort((a, b) => a.addedAt - b.addedAt)

    return groups
  })

  /**
   * sortedPricedCartGroups: Groups sorted to keep same-promo items adjacent
   * Rules:
   * 1) Groups with promo first, ordered by "first-seen" promo rank
   * 2) Within same promo, ordered by baseAddedAt ASC
   * 3) Groups without promo after, ordered by baseAddedAt ASC
   * 4) Tie-breaker: makat ASC
   */
  const sortedPricedCartGroups = computed(() => {
    const groups = pricedCartGroups.value
    if (groups.length === 0) return groups

    // Build map: makat -> group for quick lookup
    const groupsByMakat = new Map()
    for (const g of groups) {
      groupsByMakat.set(g.makat, g)
    }

    // Get base lines sorted by addedAt ASC
    const baseLines = linesArray.value.slice().sort((a, b) => {
      const aTime = a.addedAt || 0
      const bTime = b.addedAt || 0
      return aTime - bTime
    })

    // Build promoRank map: promoGroupId -> rank (first-seen order)
    const promoRank = new Map()
    for (const line of baseLines) {
      const group = groupsByMakat.get(line.makat)
      const promoId = group?.promoGroupId
      if (promoId && !promoRank.has(promoId)) {
        promoRank.set(promoId, promoRank.size)
      }
    }

    // Sort groups: stable insertion order, same-promo items stay adjacent
    const sorted = groups.slice().sort((a, b) => {
      // Primary: insertion order
      if (a.baseAddedAt !== b.baseAddedAt) return a.baseAddedAt - b.baseAddedAt

      // Secondary: group same-promo items together
      const aRank = a.promoGroupId !== null ? (promoRank.get(a.promoGroupId) ?? Infinity) : Infinity
      const bRank = b.promoGroupId !== null ? (promoRank.get(b.promoGroupId) ?? Infinity) : Infinity
      if (aRank !== bRank) return aRank - bRank

      // Tie-breaker: makat
      return a.makat.localeCompare(b.makat)
    })

    return sorted
  })

  /**
   * cartRenderBlocks: Render blocks for CartDrawer
   * Block types:
   * - { type: 'item', group } - single item card
   * - { type: 'promoGroup', promoId, promoMeta, groups, boundaryGroup, displayKey } - promo container card
   *
   * promoGroup blocks are created ONLY when a promo has a boundary unified group
   * (i.e., promo is over max and has a split item).
   */
  const cartRenderBlocks = computed(() => {
    const sorted = sortedPricedCartGroups.value
    if (sorted.length === 0) return []

    const blocks = []
    const usedMakats = new Set() // Track makats already in promoGroup blocks

    // Group by promoGroupId to detect promos with boundary items
    const promoGroupsMap = new Map() // promoId -> { groups, boundaryGroup }

    for (const group of sorted) {
      if (!group.promoGroupId) continue

      if (!promoGroupsMap.has(group.promoGroupId)) {
        promoGroupsMap.set(group.promoGroupId, {
          groups: [],
          boundaryGroup: null,
          promoMeta: null,
        })
      }

      const entry = promoGroupsMap.get(group.promoGroupId)

      if (group.isUnifiedOverMaxCard) {
        entry.boundaryGroup = group
        // Extract promo meta from boundary group's promoLine
        entry.promoMeta = {
          id: group.promoLine?.promo?.id || group.promoGroupId,
          label: group.promoLine?.promo?.label || 'מבצע',
          description: group.promoLine?.promo?.description || '',
          max: group.promoLine?.promo?.max || null,
          stepDescription: group.promoLine?.promo?.stepDescription || '',
        }
      } else {
        entry.groups.push(group)
      }
    }

    // Build blocks in sorted order
    const processedPromos = new Set()

    for (const group of sorted) {
      // Skip if already used in a promoGroup block
      if (usedMakats.has(group.makat)) continue

      const promoId = group.promoGroupId

      // Check if this promo has a boundary (over-max) scenario
      if (promoId && promoGroupsMap.has(promoId)) {
        const entry = promoGroupsMap.get(promoId)

        // Only create promoGroup block if there's a boundary item AND we haven't processed this promo yet
        if (entry.boundaryGroup && !processedPromos.has(promoId)) {
          processedPromos.add(promoId)

          // Mark all groups in this promo as used
          for (const g of entry.groups) {
            usedMakats.add(g.makat)
          }
          usedMakats.add(entry.boundaryGroup.makat)

          // Create promoGroup block
          blocks.push({
            type: 'promoGroup',
            promoId,
            promoMeta: entry.promoMeta,
            groups: entry.groups, // non-boundary groups (small cards)
            boundaryGroup: entry.boundaryGroup, // unified card at bottom
            displayKey: `promoGroup_${promoId}`,
          })
          continue
        }
      }

      // Regular item block
      blocks.push({
        type: 'item',
        group,
        displayKey: group.displayKey,
      })
    }

    return blocks
  })

  /**
   * pricedTotals: Aggregated totals computed from pricedCartLines
   */
  const pricedTotals = computed(() => {
    const lines = pricedCartLines.value

    let itemsSubtotalPreVat = 0
    let depositPreVat = 0
    let promoSavingsPreVat = 0
    let discountSavingsPreVat = 0

    for (const line of lines) {
      const qty = line.qty
      const unitPrice = line.unitPricePreVatPreDeposit
      const depositPerUnit = line.pricing.depositPerUnit

      itemsSubtotalPreVat += unitPrice * qty
      depositPreVat += depositPerUnit * qty

      // Calculate savings for promo lines
      if (line.isPromoLine && line.catalogPrice > unitPrice) {
        promoSavingsPreVat += (line.catalogPrice - unitPrice) * qty
      }

      // Calculate savings for discount lines
      if (line.isDiscountLine && line.catalogPrice > unitPrice) {
        discountSavingsPreVat += (line.catalogPrice - unitPrice) * qty
      }
    }

    const totalPreVat = itemsSubtotalPreVat + depositPreVat
    const vatAmount = totalPreVat * (VAT_MULTIPLIER - 1)
    const totalWithVat = totalPreVat * VAT_MULTIPLIER
    const totalSavingsPreVat = Math.max(0, promoSavingsPreVat + discountSavingsPreVat)

    return {
      itemsSubtotalPreVat,
      depositPreVat,
      totalPreVat,
      vatAmount,
      totalWithVat,
      promoSavingsPreVat: Math.max(0, promoSavingsPreVat),
      discountSavingsPreVat: Math.max(0, discountSavingsPreVat),
      totalSavingsPreVat,
      lineCount: lines.length,
    }
  })

  /**
   * depositSummary: Aggregated deposit info from base cart lines (no double counting)
   */
  const depositSummary = computed(() => {
    const catalogStore = useCatalogStore()
    let depositUnits = 0

    // Compute from base linesByMakat to avoid double counting from split display lines
    for (const line of linesArray.value) {
      const item = catalogStore.itemsByMakat.get(line.makat)
      if (!item?.pikadon) continue

      const pikadon = parseFloat(item.pikadon)
      if (isNaN(pikadon) || pikadon <= 0) continue

      depositUnits += line.qty * pikadon
    }

    const depositPreVat = pricedTotals.value.depositPreVat

    return {
      depositPreVat,
      depositUnits,
      hasDeposit: depositPreVat > 0,
    }
  })

  /**
   * Check if cart is empty
   */
  const isEmpty = computed(() => {
    return uniqueCount.value === 0
  })

  // ============================================
  // Internal Helpers
  // ============================================

  /**
   * Get a cart line by makat
   * @param {string} makat
   * @returns {Object|null}
   */
  function getLine(makat) {
    return linesByMakat.value[makat] || null
  }

  /**
   * Clear error state
   */
  function clearError() {
    error.value = null
  }

  /**
   * Set error and log it
   * @param {string} message
   */
  function setError(message) {
    error.value = message
    log.warn('Cart error:', message)
  }

  /**
   * Update lastChangedAt timestamp
   */
  function markChanged() {
    lastChangedAt.value = Date.now()
  }

  // ============================================
  // Actions
  // ============================================

  /**
   * Add item to cart or merge with existing line
   * @param {string} makat
   * @param {number} qty - Quantity to add (default 1)
   * @returns {{ ok: boolean, message?: string }}
   */
  function addItem(makat, qty = 1) {
    clearError()

    // Check authentication
    if (!isUserAuthenticated()) {
      setError('AUTH_REQUIRED')
      return { ok: false, message: 'AUTH_REQUIRED' }
    }

    // Get item from catalog
    const item = getCatalogItemByMakat(makat)
    if (!item) {
      setError('ITEM_NOT_FOUND')
      return { ok: false, message: 'ITEM_NOT_FOUND' }
    }

    // Check if item is out of stock (showOnWeb === 'H')
    if (item.showOnWeb === 'H') {
      setError('OUT_OF_STOCK')
      return { ok: false, message: 'OUT_OF_STOCK' }
    }

    // Block not-in-assortment items (display-only, cannot be purchased)
    if (item._notInAssortment) {
      setError('NOT_IN_ASSORTMENT')
      return { ok: false, message: 'NOT_IN_ASSORTMENT' }
    }

    // Check price exists
    if (item.price == null || item.price === '') {
      setError('PRICE_NOT_FOUND')
      return { ok: false, message: 'PRICE_NOT_FOUND' }
    }

    // Calculate new quantity (merge if exists)
    const existingLine = getLine(makat)
    const existingQty = existingLine?.qty || 0
    const newTotalQty = existingQty + qty

    // Validate quantity
    const validation = validateQtyForItem(item, newTotalQty)
    if (!validation.valid) {
      setError(validation.message)
      return { ok: false, message: validation.message }
    }

    // Add or update line
    if (existingLine) {
      // Merge: update existing line
      existingLine.qty = validation.qty
      existingLine.updatedAt = Date.now()
      log.info('Cart line updated (merge)', { makat, qty: validation.qty })
    } else {
      // New line
      linesByMakat.value[makat] = createCartLine(makat, validation.qty)
      log.info('Cart line added', { makat, qty: validation.qty })
    }

    markChanged()
    return { ok: true }
  }

  /**
   * Set quantity for a cart line
   * If qty <= 0, removes the line
   * @param {string} makat
   * @param {number} qty
   * @returns {{ ok: boolean, message?: string }}
   */
  function setQty(makat, qty) {
    clearError()

    // Check authentication
    if (!isUserAuthenticated()) {
      setError('AUTH_REQUIRED')
      return { ok: false, message: 'AUTH_REQUIRED' }
    }

    // If qty <= 0, remove the item
    if (qty <= 0) {
      return removeItem(makat)
    }

    // Check if line exists
    const existingLine = getLine(makat)
    if (!existingLine) {
      // Line doesn't exist - treat as addItem
      return addItem(makat, qty)
    }

    // Get item from catalog for validation
    const item = getCatalogItemByMakat(makat)
    if (!item) {
      setError('ITEM_NOT_FOUND')
      return { ok: false, message: 'ITEM_NOT_FOUND' }
    }

    // Validate quantity
    const validation = validateQtyForItem(item, qty)
    if (!validation.valid) {
      setError(validation.message)
      return { ok: false, message: validation.message }
    }

    // Update line
    existingLine.qty = validation.qty
    existingLine.updatedAt = Date.now()

    markChanged()
    log.info('Cart line qty set', { makat, qty: validation.qty })
    return { ok: true }
  }

  /**
   * Increment quantity for a cart line
   * For onlyBox items, increments by boxSize
   * @param {string} makat
   * @param {number} step - Ignored for onlyBox items
   * @returns {{ ok: boolean, message?: string }}
   */
  function inc(makat, step = 1) {
    clearError()

    // Check authentication
    if (!isUserAuthenticated()) {
      setError('AUTH_REQUIRED')
      return { ok: false, message: 'AUTH_REQUIRED' }
    }

    const existingLine = getLine(makat)
    if (!existingLine) {
      // Line doesn't exist - use addItem with appropriate step
      const item = getCatalogItemByMakat(makat)
      if (!item) {
        setError('ITEM_NOT_FOUND')
        return { ok: false, message: 'ITEM_NOT_FOUND' }
      }
      const actualStep = getStepForItem(item)
      return addItem(makat, actualStep)
    }

    // Get item for step calculation
    const item = getCatalogItemByMakat(makat)
    if (!item) {
      setError('ITEM_NOT_FOUND')
      return { ok: false, message: 'ITEM_NOT_FOUND' }
    }

    // Calculate actual step (boxSize for onlyBox items)
    const actualStep = getStepForItem(item)
    const newQty = existingLine.qty + actualStep

    return setQty(makat, newQty)
  }

  /**
   * Decrement quantity for a cart line
   * For onlyBox items, decrements by boxSize
   * If resulting qty <= 0, removes the line
   * @param {string} makat
   * @param {number} step - Ignored for onlyBox items
   * @returns {{ ok: boolean, message?: string }}
   */
  function dec(makat, step = 1) {
    clearError()

    // Check authentication
    if (!isUserAuthenticated()) {
      setError('AUTH_REQUIRED')
      return { ok: false, message: 'AUTH_REQUIRED' }
    }

    const existingLine = getLine(makat)
    if (!existingLine) {
      // Nothing to decrement
      return { ok: true }
    }

    // Get item for step calculation
    const item = getCatalogItemByMakat(makat)
    if (!item) {
      // Item no longer in catalog, just remove the line
      return removeItem(makat)
    }

    // Calculate actual step (boxSize for onlyBox items)
    const actualStep = getStepForItem(item)
    const newQty = existingLine.qty - actualStep

    if (newQty <= 0) {
      return removeItem(makat)
    }

    return setQty(makat, newQty)
  }

  /**
   * Remove item from cart
   * @param {string} makat
   * @returns {{ ok: boolean }}
   */
  function removeItem(makat) {
    clearError()

    // Check authentication
    if (!isUserAuthenticated()) {
      setError('AUTH_REQUIRED')
      return { ok: false, message: 'AUTH_REQUIRED' }
    }

    if (linesByMakat.value[makat]) {
      delete linesByMakat.value[makat]
      markChanged()
      log.info('Cart line removed', { makat })
    }

    return { ok: true }
  }

  /**
   * Clear all items from cart
   * Also clears saved cart from Firestore
   * @returns {{ ok: boolean }}
   */
  function clearCart() {
    clearError()

    // Check authentication
    if (!isUserAuthenticated()) {
      setError('AUTH_REQUIRED')
      return { ok: false, message: 'AUTH_REQUIRED' }
    }

    const count = uniqueCount.value
    linesByMakat.value = {}
    markChanged()
    log.info('Cart cleared', { removedCount: count })

    // Also clear from Firestore (fire and forget)
    clearSavedCartFromFirestore()

    return { ok: true }
  }

  /**
   * Reset cart on logout (internal use)
   * Does NOT require authentication check
   */
  function resetOnLogout() {
    linesByMakat.value = {}
    createdAt.value = Date.now()
    error.value = null
    lastChangedAt.value = null
    log.info('Cart reset on logout')
  }

  /**
   * Get quantity in cart for a specific makat
   * @param {string} makat
   * @returns {number}
   */
  function getQty(makat) {
    const line = getLine(makat)
    return line?.qty || 0
  }

  /**
   * Check if makat is in cart
   * @param {string} makat
   * @returns {boolean}
   */
  function hasItem(makat) {
    return !!linesByMakat.value[makat]
  }

  // ============================================
  // Cart Persistence (Firestore)
  // ============================================

  /**
   * Schedule a debounced save to Firestore
   * Called automatically when cart changes
   */
  function scheduleSave() {
    // Don't save during restoration
    if (isRestorationInProgress) return

    const authStore = useAuthStore()
    const userId = authStore.userId
    if (!userId) return

    // Don't save for agent users viewing client catalogs
    const catalogStore = useCatalogStore()
    if (catalogStore.catalogMode === 'agent') return

    // Clear existing timeout
    if (saveTimeout) {
      clearTimeout(saveTimeout)
    }

    // Debounce save by 1 second
    saveTimeout = setTimeout(async () => {
      try {
        await saveCart(userId, linesArray.value)
        log.info('Cart saved to Firestore', { lineCount: linesArray.value.length })
      } catch (err) {
        log.warn('Failed to save cart to Firestore', err)
      }
    }, 1000)
  }

  /**
   * Restore cart from Firestore
   * Called after catalog bootstrap completes
   * Validates each item against current catalog before adding
   */
  async function restoreFromFirestore() {
    const authStore = useAuthStore()
    const userId = authStore.userId
    if (!userId) return

    // Don't restore if cart already has items
    if (Object.keys(linesByMakat.value).length > 0) return

    // Don't restore for agent users viewing client catalogs
    const catalogStore = useCatalogStore()
    if (catalogStore.catalogMode === 'agent') {
      return
    }

    isRestoring.value = true
    isRestorationInProgress = true
    restorationIssues.value = []

    try {
      const saved = await loadSavedCart(userId)
      if (!saved || !saved.lines?.length) {
        log.info('No saved cart to restore')
        return
      }

      log.info('Restoring cart from Firestore', { lineCount: saved.lines.length })
      const issues = []
      let restoredCount = 0

      for (const line of saved.lines) {
        const item = catalogStore.itemsByMakat.get(line.makat)

        // Check if item exists in current catalog
        if (!item) {
          issues.push({ makat: line.makat, name: line.makat, reason: 'not_found' })
          continue
        }

        // Check if item has price
        if (item.price == null || item.price === '') {
          issues.push({ makat: line.makat, name: item.name || line.makat, reason: 'no_price' })
          continue
        }

        // Check quantity validity (boxSize rules)
        const qty = line.qty
        // onlyBox === 0 means must order in box multiples
        const isOnlyBox =
          item.onlyBox == null ||
          item.onlyBox === 0 ||
          item.onlyBox === '0' ||
          item.onlyBox === false
        const boxSize = parseInt(item.boxSize) || 1

        if (isOnlyBox && boxSize > 0 && qty % boxSize !== 0) {
          issues.push({ makat: line.makat, name: item.name || line.makat, reason: 'invalid_qty' })
          continue
        }

        // Add to cart (without triggering save)
        linesByMakat.value[line.makat] = {
          makat: line.makat,
          qty,
          addedAt: line.addedAt || Date.now(),
          updatedAt: Date.now(),
          promo: { eligiblePromotionIds: [], appliedPromotionId: null, meta: {} },
        }
        restoredCount++
      }

      restorationIssues.value = issues

      // Show success toast if items were restored
      if (restoredCount > 0) {
        success('העגלה שלך שוחזרה בהצלחה')
        log.info('Cart restored successfully', { restoredCount, issuesCount: issues.length })
      }

      // Show warning if some items were removed
      if (issues.length > 0) {
        const names = issues
          .map((i) => i.name)
          .slice(0, 3)
          .join(', ')
        const suffix = issues.length > 3 ? ` ועוד ${issues.length - 3}` : ''
        warning(`חלק מהפריטים הוסרו מהעגלה: ${names}${suffix}`, 5000)
        log.info('Some cart items could not be restored', { issues })
      }
    } catch (err) {
      log.warn('Failed to restore cart from Firestore', err)
    } finally {
      isRestoring.value = false
      isRestorationInProgress = false
    }
  }

  /**
   * Clear saved cart from Firestore
   * Called after successful checkout
   */
  async function clearSavedCartFromFirestore() {
    const authStore = useAuthStore()
    const userId = authStore.userId
    if (!userId) return

    try {
      await clearSavedCart(userId)
      log.info('Saved cart cleared from Firestore')
    } catch (err) {
      log.warn('Failed to clear saved cart from Firestore', err)
    }
  }

  // Watch for cart changes and auto-save
  watch(
    linesByMakat,
    () => {
      scheduleSave()
    },
    { deep: true }
  )

  // ============================================
  // Return Public API
  // ============================================

  // ============================================
  // Cart UI State
  // ============================================

  /**
   * Set cart drawer open state (for tutorial system)
   */
  function setCartOpen(open) {
    isCartOpen.value = open
  }

  return {
    // State
    linesByMakat,
    createdAt,
    error,
    lastChangedAt,
    isRestoring,
    restorationIssues,
    isCartOpen,

    // Getters
    linesArray,
    uniqueCount,
    itemsCount,
    subtotal,
    subtotalWithoutPromo,
    promoSavings,
    pricedCartLines,
    pricedCartGroups,
    sortedPricedCartGroups,
    cartRenderBlocks,
    pricedTotals,
    depositSummary,
    isEmpty,

    // Actions
    getLine,
    getQty,
    hasItem,
    getLinePriceInfo,
    addItem,
    setQty,
    inc,
    dec,
    removeItem,
    clearCart,
    clearError,
    resetOnLogout,
    restoreFromFirestore,
    clearSavedCartFromFirestore,
    setCartOpen,
  }
})
